import type { FC } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, type IntlShape, useIntl } from 'react-intl';

import type { TriggerProps as PopupTriggerProps } from '@atlaskit/popup/types';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import type { TriggerProps as TooltipTriggerProps } from '@atlaskit/tooltip/types';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { usePageTitle } from '@confluence/content-title/entry-points/usePageTitle.ts';
import { withSmartCardProviderContext } from '@confluence/editor-features/entry-points/withSmartCardProviderContext.ts';
import { type DevDocsBylineConnectionAncestor as DevDocsBylineConnectionAncestorType } from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import {
	ANALYTICS_SOURCE,
	BYLINE_ACTION_SUBJECT_ID,
	EMBEDDED_MENU_SOURCE,
	MENU_CLOSE_ACTION_SUBJECT_ID,
	MENU_SOURCE,
} from './analytics.ts';
import { BylineConnectionsTrigger, BylineConnectionsWrapper } from './BylineConnectionsTrigger.tsx';
import { BylineConnectionsPlaceholder } from './ssr/BylineConnectionsPlaceholder.tsx';
import { getReservedBylineIconCount } from './ssr/outboundBylineSsrState.ts';
import { useOutboundBylineSsrState } from './ssr/useOutboundBylineSsrState.ts';
import { useIncomingLinksSsrState } from './ssr/useIncomingLinksSsrState.ts';
import {
	getBylineConnectionIcons,
	MAX_BYLINE_CONNECTION_ICONS,
} from './utils/getBylineConnectionIcons.ts';
import { ConnectionsByLineMenu } from './ConnectionsByLineMenu.tsx';
import { EmbeddedConnectionsByLineMenu } from './EmbeddedConnectionsByLineMenu.tsx';
import { useBylineConnectionsData } from './hooks/useBylineConnectionsData.ts';
import { useBylineConnectionsLoadTiming } from './hooks/useBylineConnectionsLoadTiming.ts';
import { useTeamworkGraphPreviewPanel } from './teamwork-graph-preview-panel/useTeamworkGraphPreviewPanel.tsx';
import type { BylineContentType as BylineContentTypeType } from './utils/contentAri.ts';
import { getBylineVisibility } from './utils/getBylineVisibility.ts';
import { getDevDocsConnectionIcons } from './utils/getDevDocsConnectionIcons.ts';
import { useTwgAnalyticsEvents } from './useTwgAnalyticsEvents.ts';

const i18n = defineMessages({
	tooltipTwg: {
		id: 'byline-connections.tooltip-twg-v2',
		defaultMessage: "See how this content connects to your team's work",
		description:
			"Tooltip shown on hover over the connections byline item, describing that it shows how the current page connects to the team's work.",
	},
});

/**
 * Forwards a node to a React ref that may be either a callback ref or an object
 * ref. Exported for unit testing so both ref shapes are covered.
 */
export const assignRef = (
	ref: React.Ref<HTMLButtonElement>,
	node: HTMLButtonElement | null,
): void => {
	if (typeof ref === 'function') {
		ref(node);
	} else if (ref && 'current' in ref) {
		(ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
	}
};

/**
 * Resolves the byline tooltip text. Returns an empty string while the menu is
 * open (the tooltip is suppressed then), otherwise the Teamwork Graph label.
 */
const getTooltipContent = (isOpen: boolean, formatMessage: IntlShape['formatMessage']): string => {
	if (isOpen) {
		return '';
	}
	return formatMessage(i18n.tooltipTwg);
};

export type BylineConnectionsProps = {
	contentId: string;
	/**
	 * Pages and blog posts are distinct Teamwork Graph node types, so every graph query and the
	 * At-a-glance request are keyed on this.
	 */
	contentType: BylineContentType;
	connectionAncestors?: readonly DevDocsBylineConnectionAncestor[] | null;
	pageVersion?: number | null;
	pageUpdatedAt?: string | null;
};

export type DevDocsBylineConnectionAncestor = DevDocsBylineConnectionAncestorType;
export type BylineContentType = BylineContentTypeType;

const BylineConnectionsInner: FC<BylineConnectionsProps> = ({
	contentId,
	contentType,
	connectionAncestors,
	pageVersion,
	pageUpdatedAt,
}) => {
	const { title, error: pageTitleError } = usePageTitle({ contentId });
	const { formatMessage } = useIntl();
	const { Popup } = useChoreographerAssets();
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const { isTeamworkGraphPreviewPanelOpen, openTeamworkGraphPreviewPanel } =
		useTeamworkGraphPreviewPanel(contentId, contentType);
	const [menuState, setMenuState] = useState({ contentId, isOpen: false, hasOpenedPanel: false });
	if (menuState.contentId !== contentId) {
		setMenuState({ contentId, isOpen: false, hasOpenedPanel: isTeamworkGraphPreviewPanelOpen });
	} else if (isTeamworkGraphPreviewPanelOpen && !menuState.hasOpenedPanel) {
		setMenuState({ ...menuState, hasOpenedPanel: true });
	}
	const isCurrentPage = menuState.contentId === contentId;
	const isOpen = isCurrentPage && menuState.isOpen;
	// The popup's focus trap returns focus to the trigger on close by calling
	// `.focus()` (without `preventScroll`), which scrolls the trigger back into
	// view. For a link navigation that fights the in-page scroll, so we disable
	// the trap's focus return (`shouldReturnFocus={false}`) and instead restore
	// focus ourselves on non-navigation closes (Esc / outside click) using
	// `preventScroll` — keeping keyboard a11y without the scroll jump.
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const navigatedRef = useRef(false);
	const hasFiredViewedRef = useRef(false);
	const { outgoing, incoming, relatedContent, devDocsConnection } = useBylineConnectionsData({
		contentId,
		contentType,
		connectionAncestors,
		pageVersion,
		pageUpdatedAt,
		pageTitle: pageTitleError ? null : title,
		atAGlanceEnabled: isCurrentPage && menuState.hasOpenedPanel,
	});
	const { loading: outgoingLinksLoading, links: outgoingLinks, refreshLinkUrl } = outgoing;
	// Remote-link rows cannot be server-side rendered and land after first paint, so only the
	// Teamwork Graph rows decide visibility and the painted icons; the menu lists every row. CCINT-9346
	const {
		links: incomingLinks,
		twgLinks: twgIncomingLinks,
		loading: incomingLinksLoading,
		settled: incomingLinksSettled,
		hasNextPage: incomingLinksHaveMore,
	} = incoming;
	const { links: relatedContentLinks, loading: relatedContentLoading } = relatedContent;
	// Embedded Confluence has no preview panel, so it gets a menu that never opens one.
	// Either the byline experiment or the planner gate turns it on.
	const isEmbeddedMenu =
		useIsEmbeddedConfluence_DO_NOT_USE() &&
		(isExperimentEnabled('confluence_1p_and_3p_connection_byline_experiment') ||
			fg('migrate_planner_to_docs_tab'));
	const devDocsIcons = useMemo(
		() => getDevDocsConnectionIcons(devDocsConnection),
		[devDocsConnection],
	);
	// Outgoing links fill the stack first, so once they alone fill it no incoming icon can change
	// what is painted and the trigger need not wait for the graph query.
	const outgoingFillsStack = useMemo(
		() =>
			getBylineConnectionIcons(outgoingLinks, devDocsIcons).length >= MAX_BYLINE_CONNECTION_ICONS,
		[outgoingLinks, devDocsIcons],
	);

	const hasOutboundConnection = outgoingLinks.length > 0 || devDocsConnection !== null;
	const outboundState = useOutboundBylineSsrState(contentId);
	const incomingState = useIncomingLinksSsrState(contentId);
	const reservedIconCount = getReservedBylineIconCount(
		outboundState,
		devDocsConnection !== null,
		incomingState?.status === 'present',
	);
	// The show/hide and wait rules for both menus live in `getBylineVisibility`.
	const { hasConnection, isResolving, shouldRenderByline } = useMemo(
		() =>
			getBylineVisibility({
				isEmbeddedMenu,
				outgoingLinks,
				incomingLinks: twgIncomingLinks,
				relatedContentLinks,
				hasDevDocsConnection: devDocsConnection !== null,
				outgoingLinksLoading,
				incomingLinksLoading,
				relatedContentLoading,
				outgoingFillsStack,
				reservedIconCount,
			}),
		[
			isEmbeddedMenu,
			outgoingLinks,
			twgIncomingLinks,
			relatedContentLinks,
			devDocsConnection,
			outgoingLinksLoading,
			incomingLinksLoading,
			relatedContentLoading,
			outgoingFillsStack,
			reservedIconCount,
		],
	);
	useBylineConnectionsLoadTiming(
		contentId,
		isResolving,
		hasConnection,
		outgoing.error || (!hasOutboundConnection ? incoming.error : null),
	);

	// Fire a single "viewed" event the first time the byline actually renders its
	// content — skip while resolving and while there is nothing to show (both cases
	// early-return before the trigger is visible).
	useEffect(() => {
		if (hasFiredViewedRef.current || isResolving || !shouldRenderByline) {
			return;
		}
		hasFiredViewedRef.current = true;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'byline',
				actionSubjectId: BYLINE_ACTION_SUBJECT_ID,
				source: ANALYTICS_SOURCE,
			},
		}).fire();
	}, [createAnalyticsEvent, isResolving, shouldRenderByline]);

	const handleClose = useCallback(() => {
		setMenuState((current) => ({ ...current, isOpen: false }));
		// Navigating away lands here too — the popup fires `onClose` once the panel
		// opens — and that is not a dismissal: it reports its own event, and focus belongs to
		// whatever is opening rather than back on the trigger.
		if (navigatedRef.current) {
			return;
		}
		// What is left is every real dismissal — Esc and outside clicks — so this is
		// one close event per close, mirroring the modals.
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: MENU_CLOSE_ACTION_SUBJECT_ID,
				source: isEmbeddedMenu ? EMBEDDED_MENU_SOURCE : MENU_SOURCE,
			},
		}).fire();
		// Esc / outside-click close: return focus to the trigger without scrolling.
		triggerRef.current?.focus({ preventScroll: true });
	}, [createAnalyticsEvent, isEmbeddedMenu]);
	const handleNavigate = useCallback(() => {
		navigatedRef.current = true;
		setMenuState((current) => ({ ...current, isOpen: false }));
	}, []);
	const handleOpenPreviewPanel = useCallback(() => {
		navigatedRef.current = true;
		openTeamworkGraphPreviewPanel();
		setMenuState((current) => ({ ...current, isOpen: false }));
	}, [openTeamworkGraphPreviewPanel]);
	const handleToggle = useCallback(() => {
		navigatedRef.current = false;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: BYLINE_ACTION_SUBJECT_ID,
				source: ANALYTICS_SOURCE,
			},
		}).fire();
		setMenuState((current) => ({ ...current, isOpen: !isOpen }));
	}, [createAnalyticsEvent, isOpen]);

	const handlePlaceholderOpenChange = useCallback((isOpen: boolean) => {
		if (isOpen) {
			navigatedRef.current = false;
		}
		setMenuState((current) => ({ ...current, isOpen }));
	}, []);

	if (isResolving) {
		// Keep three placeholder slots across SSR, chunk loading and data loading whenever a
		// preloaded connection signal exists. The visibility rules above still decide when the
		// loaded trigger can appear and whether the page reaches the row minimum. CCINT-9354
		return (
			<BylineConnectionsPlaceholder
				contentId={contentId}
				contentType={contentType}
				connectionAncestors={connectionAncestors}
				isOpen={isOpen}
				onOpenChange={handlePlaceholderOpenChange}
			/>
		);
	}

	if (!shouldRenderByline) {
		return null;
	}

	return (
		<BylineConnectionsWrapper>
			<Popup
				isOpen={isOpen}
				onClose={handleClose}
				placement="bottom-start"
				shouldReturnFocus={false}
				content={() =>
					isEmbeddedMenu ? (
						<EmbeddedConnectionsByLineMenu
							links={outgoingLinks}
							relatedContentLinks={relatedContentLinks}
							relatedContentLoading={relatedContentLoading}
							refreshLinkUrl={refreshLinkUrl}
							onNavigate={handleNavigate}
						/>
					) : (
						<ConnectionsByLineMenu
							links={outgoingLinks}
							incomingLinks={incomingLinks}
							incomingLinksHaveMore={incomingLinksHaveMore}
							incomingLinksSettled={incomingLinksSettled}
							additionalIcons={devDocsIcons}
							minIcons={MAX_BYLINE_CONNECTION_ICONS}
							relatedContentLinks={relatedContentLinks}
							relatedContentLoading={relatedContentLoading}
							devDocsConnection={devDocsConnection}
							contentId={contentId}
							showFooter={outgoingLinks.length > 0}
							refreshLinkUrl={refreshLinkUrl}
							onNavigate={handleNavigate}
							onOpenPreviewPanel={handleOpenPreviewPanel}
						/>
					)
				}
				trigger={({ ref, ...triggerProps }: PopupTriggerProps) => (
					<Tooltip content={getTooltipContent(isOpen, formatMessage)}>
						{({
							ref: tooltipRef,
							onClick: onTooltipClick,
							...tooltipProps
						}: TooltipTriggerProps) => (
							<BylineConnectionsTrigger
								appearance="loaded"
								links={outgoingLinks}
								incomingLinks={twgIncomingLinks}
								additionalIcons={devDocsIcons}
								minIcons={MAX_BYLINE_CONNECTION_ICONS}
								buttonProps={{
									...tooltipProps,
									...triggerProps,
									ref: (node: HTMLButtonElement | null) => {
										triggerRef.current = node;
										assignRef(ref, node);
										tooltipRef(node);
									},
									onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
										onTooltipClick(event);
										handleToggle();
									},
									'aria-expanded': isOpen,
									'aria-haspopup': 'dialog',
								}}
							/>
						)}
					</Tooltip>
				)}
			/>
		</BylineConnectionsWrapper>
	);
};

export const BylineConnections: (props: any) => React.JSX.Element =
	withSmartCardProviderContext(BylineConnectionsInner);
